import React, { useCallback, useEffect, useRef } from 'react';
import { Sparkles, Trash2, Loader, Check, X, Copy, Lightbulb, Send, AlertTriangle } from 'lucide-react';
import { useQuery } from 'react-query';
import { useAskSidebar, type ChatMessage, type SidebarMode, type AgentToolCall } from '../../contexts/AskSidebarContext';
import { ChatThread } from '../ask/ChatThread';
import { ChatInput } from '../ask/ChatInput';
import { SelectedContextChips } from '../ask/SelectedContextChips';
import { Logo } from '../Logo';
import { reportApi, agentApi, getCaseIndexingStatus } from '../../services/reportApi';
import { markdownToHtml } from '../../utils/markdownToProseMirror';
import { GUIDELINES } from '../../data/guidelines';

const guidelineMap = new Map(GUIDELINES.map(g => [g.id, g]));
function enrichGuidelineIds(ids: string[]): string[] {
  return ids.map(id => {
    const g = guidelineMap.get(id);
    return g ? `${g.id}::${g.title}` : id;
  });
}

/**
 * Assistant tab content — extracted from the former ExpandedSidebar in UnifiedAskSidebar.
 * Contains AI ask, revise, and write modes.
 */
export const AssistantTab: React.FC = () => {
  const {
    pageContext, mode, setMode, availableModes,
    messages, setMessages, isLoading, setIsLoading, clearMessages,
    contextItems, clearContext, textSelection, setTextSelection,
    streamedText, setStreamedText, isStreaming, setIsStreaming,
    generationComplete, setGenerationComplete,
    pendingMessage, clearPendingMessage,
    selectedDocumentIds, selectedGuidelineIds, selectedObjectiveIds,
    conversationId, setConversationId,
    activeTabId, updateTab, createTab, closeTab,
  } = useAskSidebar();

  const outputRef = useRef<HTMLDivElement>(null);
  const instructionRef = useRef<HTMLTextAreaElement>(null);
  const [instruction, setInstruction] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  const editor = pageContext.editor;
  const editorSelectedText = pageContext.editorSelectedText;

  const { data: indexingStatus } = useQuery(
    ['indexingStatus', pageContext.caseId],
    () => getCaseIndexingStatus(pageContext.caseId!),
    { enabled: !!pageContext.caseId, staleTime: 30_000, retry: false }
  );
  const isIndexed = indexingStatus?.isIndexed ?? true;

  useEffect(() => {
    if (editorSelectedText && !isStreaming) {
      setMode('revise-selection');
    } else if (!editorSelectedText && mode === 'revise-selection') {
      setMode('revise');
    }
  }, [editorSelectedText, isStreaming, mode, setMode]);

  useEffect(() => {
    if (outputRef.current && isStreaming) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [streamedText, isStreaming]);

  // ─── Ask mode handler ─────────────────────────────────────────────────────

  const buildContextPrefix = useCallback((): string => {
    const parts: string[] = [];
    if (contextItems.length > 0) {
      parts.push('Context — Selected items:');
      contextItems.forEach(item => {
        parts.push(`[${item.type}] ${item.summary}`);
        if (item.data) {
          const d = item.data;
          if (d.timestamp) parts.push(`  Date: ${typeof d.timestamp === 'object' ? JSON.stringify(d.timestamp) : d.timestamp}`);
          if (d.type) parts.push(`  Category: ${d.type}`);
          if (d.description && d.description !== item.summary) parts.push(`  Full description: ${d.description}`);
          const skip = new Set(['description', 'timestamp', 'type', 'eventId']);
          for (const [key, val] of Object.entries(d)) {
            if (!skip.has(key) && (typeof val === 'string' || typeof val === 'number')) {
              parts.push(`  ${key}: ${val}`);
            }
          }
        }
      });
      parts.push('');
    }
    if (textSelection) {
      parts.push(`Selected text: "${textSelection.text}"`);
      parts.push('');
    }
    return parts.join('\n');
  }, [contextItems, textSelection]);

  const handleSendMessage = useCallback(async (content: string) => {
    if (!pageContext.caseId) return;

    const sendingTabId = activeTabId;
    if (!sendingTabId) return;

    const contextPrefix = buildContextPrefix();
    const fullQuestion = contextPrefix ? `${contextPrefix}Question: ${content}` : content;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    const assistantMsgId = (Date.now() + 1).toString();
    const assistantMessage: ChatMessage = {
      id: assistantMsgId,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isStreaming: true,
      isThinking: true,
      toolCalls: [],
    };

    let localMessages = [...messages, userMessage, assistantMessage];
    updateTab(sendingTabId, { messages: [...localMessages], isStreaming: true });

    const patchLastAssistant = (patch: Partial<ChatMessage>) => {
      const last = localMessages[localMessages.length - 1];
      if (last?.id === assistantMsgId) {
        localMessages = [...localMessages.slice(0, -1), { ...last, ...patch }];
        updateTab(sendingTabId, { messages: [...localMessages] });
      }
    };

    try {
      let accumulated = '';
      const currentToolCalls: AgentToolCall[] = [];

      await agentApi.chat(
        {
          message: fullQuestion,
          caseId: pageContext.caseId!,
          conversationId,
        },
        {
          onThinking: () => {
            patchLastAssistant({ isThinking: true });
          },
          onToolCall: (event) => {
            currentToolCalls.push({
              toolName: event.toolName,
              toolUseId: event.toolUseId,
              input: event.input,
              status: 'running',
            });
            patchLastAssistant({ isThinking: false, toolCalls: [...currentToolCalls] });
          },
          onToolResult: (event) => {
            const tc = currentToolCalls.find(t => t.toolUseId === event.toolUseId);
            if (tc) {
              tc.status = 'done';
              tc.summary = event.summary;
              if (event.widgetData) {
                tc.widgetData = event.widgetData;
              }
            }
            patchLastAssistant({ toolCalls: [...currentToolCalls] });
          },
          onToken: (token) => {
            accumulated += token;
            patchLastAssistant({ content: accumulated, isThinking: false });
          },
          onSources: (sources) => {
            patchLastAssistant({
              sources: sources.map(s => ({
                documentId: s.documentId || '',
                documentName: s.documentName || 'Unknown',
                pageNumber: s.pageNumber,
                excerpt: s.excerpt,
                sourceText: s.sourceText,
                chunkIndex: s.chunkIndex,
              })),
            });
          },
          onDone: (event) => {
            if (event.conversationId) {
              updateTab(sendingTabId, { conversationId: event.conversationId });
            }
            patchLastAssistant({ isStreaming: false, isThinking: false });
            updateTab(sendingTabId, { isStreaming: false });
          },
          onError: (err) => {
            console.error('[Agent] Stream error:', err);
            patchLastAssistant({
              isStreaming: false,
              isThinking: false,
              content: accumulated || 'Sorry, I encountered an error. Please try again.',
            });
            updateTab(sendingTabId, { isStreaming: false });
          },
        },
      );
    } catch (err) {
      console.error('[Agent] Error:', err);
      updateTab(sendingTabId, {
        messages: [...messages, userMessage, {
          id: (Date.now() + 2).toString(),
          role: 'assistant',
          content: 'Sorry, I encountered an error. Please try again.',
          timestamp: new Date(),
        }],
        isLoading: false,
        isStreaming: false,
      });
    } finally {
      updateTab(sendingTabId, { isLoading: false });
    }
  }, [pageContext, setMessages, setIsLoading, buildContextPrefix, conversationId, activeTabId, updateTab, messages]);

  useEffect(() => {
    if (pendingMessage && !isLoading) {
      handleSendMessage(pendingMessage);
      clearPendingMessage();
    }
  }, [pendingMessage, isLoading, handleSendMessage, clearPendingMessage]);

  // ─── Revise/Write mode handler ────────────────────────────────────────────

  const handleRevisionSend = useCallback(() => {
    if (!instruction.trim() || isStreaming || !pageContext.reportId) return;

    setError(null);
    setStreamedText('');
    setIsStreaming(true);
    setGenerationComplete(false);

    const contextFields = {
      ...(pageContext.caseId && { caseId: pageContext.caseId }),
      ...(selectedDocumentIds.length > 0 && { documentIds: selectedDocumentIds }),
      ...(selectedGuidelineIds.length > 0 && { guidelineIds: enrichGuidelineIds(selectedGuidelineIds) }),
      ...(selectedObjectiveIds.length > 0 && { objectiveIds: selectedObjectiveIds }),
    };

    const onToken = (token: string) => setStreamedText(prev => prev + token);
    const onDone = () => { setIsStreaming(false); setGenerationComplete(true); };
    const onError = (err: Error) => { setIsStreaming(false); setError(err.message); };

    if (mode === 'revise') {
      reportApi.reviseReportStream(pageContext.reportId, { instruction: instruction.trim(), ...contextFields }, onToken, onDone, onError);
    } else if (mode === 'revise-selection' && editor) {
      const { from, to } = editor.state.selection;
      const text = editor.state.doc.textBetween(from, to, ' ');
      const beforeStart = Math.max(0, from - 200);
      const afterEnd = Math.min(editor.state.doc.content.size, to + 200);
      reportApi.reviseSelectionStream(
        pageContext.reportId,
        { selectedText: text, instruction: instruction.trim(), beforeContext: editor.state.doc.textBetween(beforeStart, from, ' '), afterContext: editor.state.doc.textBetween(to, afterEnd, ' '), ...contextFields },
        onToken, onDone, onError,
      );
    } else if (mode === 'write' && editor) {
      const { from } = editor.state.selection;
      const beforeStart = Math.max(0, from - 300);
      const afterEnd = Math.min(editor.state.doc.content.size, from + 300);
      const beforeText = editor.state.doc.textBetween(beforeStart, from, ' ');
      const afterText = editor.state.doc.textBetween(from, afterEnd, ' ');
      const insertionContext = beforeText || afterText ? `${beforeText}\n[CURSOR]\n${afterText}` : undefined;
      reportApi.generateContentStream(
        pageContext.reportId,
        { caseId: pageContext.caseId!, instruction: instruction.trim(), insertionContext, documentIds: selectedDocumentIds.length > 0 ? selectedDocumentIds : undefined, guidelineIds: selectedGuidelineIds.length > 0 ? enrichGuidelineIds(selectedGuidelineIds) : undefined, objectiveIds: selectedObjectiveIds.length > 0 ? selectedObjectiveIds : undefined },
        onToken, onDone, onError,
      );
    }
  }, [instruction, isStreaming, mode, pageContext, editor, selectedDocumentIds, selectedGuidelineIds, selectedObjectiveIds, setStreamedText, setIsStreaming, setGenerationComplete]);

  const handleAccept = useCallback(() => {
    if (!streamedText || !editor) return;
    if (mode === 'revise') {
      try {
        JSON.parse(streamedText);
        editor.commands.setContent(JSON.parse(streamedText), { emitUpdate: false });
      } catch {
        editor.commands.setContent(markdownToHtml(streamedText), { emitUpdate: false });
      }
    } else if (mode === 'revise-selection') {
      editor.chain().focus().deleteSelection().insertContent(markdownToHtml(streamedText)).run();
    } else if (mode === 'write') {
      editor.chain().focus().insertContent(markdownToHtml(streamedText)).run();
    }
    resetRevisionState();
  }, [streamedText, editor, mode]);

  const resetRevisionState = useCallback(() => {
    setStreamedText('');
    setGenerationComplete(false);
    setError(null);
    setInstruction('');
  }, [setStreamedText, setGenerationComplete]);

  const handleRevisionKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleRevisionSend();
    }
  };

  const showSuggestions = messages.length === 0;
  const isRevisionMode = mode === 'revise' || mode === 'revise-selection' || mode === 'write';

  const modeLabel = mode === 'ask' ? 'Ask' : mode === 'revise' ? 'Revise' : mode === 'revise-selection' ? 'Selection' : 'Write';

  const placeholder = mode === 'revise'
    ? 'e.g. "Make the tone more formal"'
    : mode === 'revise-selection'
      ? 'e.g. "Rewrite to emphasise the delayed diagnosis"'
      : mode === 'write'
        ? 'e.g. "Describe the MRI scan findings"'
        : 'Ask a question...';

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-medical-200 dark:border-medical-700 bg-white/80 dark:bg-medical-800/80">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-lg bg-primary-600 dark:bg-primary-700 flex items-center justify-center shadow-sm">
            <Sparkles className="h-3.5 w-3.5 text-white" />
          </div>
          <span className="text-sm font-semibold text-medical-800 dark:text-medical-200">ClinLexis Assistant</span>
          <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-primary-100 text-primary-600 dark:bg-primary-900/30 dark:text-medical-300">{modeLabel}</span>
        </div>
        <div className="flex items-center gap-1">
          {(messages.length > 0 || streamedText) && (
            <button
              onClick={() => { if (activeTabId) { closeTab(activeTabId); createTab(); } resetRevisionState(); }}
              className="p-1 text-medical-400 hover:text-red-500 dark:hover:text-red-400 rounded"
              title="Clear conversation"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>


      {/* Not-indexed warning */}
      {!isIndexed && mode === 'ask' && (
        <div className="mx-3 mt-3 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5 text-xs text-amber-800 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <span>Documents not yet indexed. Run analysis on this case to enable the ClinLexis Assistant.</span>
        </div>
      )}

      {/* Content area */}
      <div className="flex-1 overflow-y-auto p-4" ref={outputRef}>
        {mode === 'ask' ? (
          messages.length === 0 && !isLoading ? (
            <div className="flex flex-col items-center justify-center h-full text-center px-6 pb-8">
              <div className="w-12 h-12 rounded-2xl bg-primary-600 dark:bg-primary-700 flex items-center justify-center mb-4 shadow-lg shadow-primary-500/20 dark:shadow-none">
                <Sparkles className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-sm font-semibold text-medical-700 dark:text-medical-200 mb-1">How can I help?</h3>
              <p className="text-xs text-medical-400 dark:text-medical-500 leading-relaxed max-w-[16rem]">
                {pageContext.pageType === 'report-viewer' || pageContext.pageType === 'report-editor'
                  ? 'Ask questions about the source documents and case data behind this report.'
                  : 'Query across all documents, timeline events, and analysis findings for this case.'}
              </p>
            </div>
          ) : (
            <ChatThread messages={messages} isLoading={isLoading} />
          )
        ) : isRevisionMode ? (
          (isStreaming || generationComplete) && streamedText ? (
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                {isStreaming ? (
                  <>
                    <div className="animate-pulse">
                      <Logo size="sm" showText={false} />
                    </div>
                    <span className="text-xs font-medium text-primary-600">{modeLabel}...</span>
                  </>
                ) : (
                  <>
                    <Sparkles className="h-3.5 w-3.5 text-green-500" />
                    <span className="text-xs font-medium text-green-600">Complete</span>
                  </>
                )}
              </div>
              <div
                className="ai-preview bg-white dark:bg-medical-700 border border-medical-200 dark:border-medical-600 rounded-lg p-3 text-[13px] text-medical-700 dark:text-medical-200 leading-relaxed max-h-[60vh] overflow-y-auto"
                dangerouslySetInnerHTML={{ __html: markdownToHtml(streamedText) }}
              />
              {generationComplete && (
                  <div className="flex items-center gap-2 mt-3">
                    <button onClick={handleAccept} className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700">
                      <Check className="h-4 w-4" />Accept
                    </button>
                    <button onClick={resetRevisionState} className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-medical-700 bg-medical-100 rounded-lg hover:bg-medical-200">
                      <X className="h-4 w-4" />Discard
                    </button>
                  </div>
              )}
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-600">
              {error}
              <button onClick={() => setError(null)} className="block mt-2 text-xs text-red-500 underline">Dismiss</button>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center text-medical-400">
              <Sparkles className="h-8 w-8 mb-3 text-primary-100 dark:text-primary-900" />
              <p className="text-sm font-medium text-medical-500 mb-1">
                {mode === 'write' ? 'Write from Sources' : 'AI-Powered Editing'}
              </p>
              <p className="text-xs leading-relaxed max-w-[14rem]">
                {mode === 'write'
                  ? 'Select sources above, describe what to write, and press Send.'
                  : 'Type an instruction below to revise the report using AI.'}
              </p>
            </div>
          )
        ) : null}
      </div>

      {/* Context chips */}
      <SelectedContextChips />

      {/* Input area */}
      <div className="border-t border-medical-200 dark:border-medical-700 bg-white dark:bg-medical-800 p-3 space-y-2">
        {mode === 'ask' ? (
          <ChatInput
            onSend={handleSendMessage}
            isLoading={isLoading || !isIndexed}
            placeholder={!isIndexed ? 'Run analysis to enable search...' : 'Ask anything — the AI will search, read, and analyse...'}
            suggestedQuestions={['Summarise the key events', 'Are there gaps in the records?', 'What are the most significant findings?']}
            showSuggestions={showSuggestions && isIndexed}
          />
        ) : (
          <>
            {mode === 'revise-selection' && editorSelectedText && (
              <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-100 dark:border-primary-900 rounded px-2.5 py-1.5 text-xs text-primary-700 dark:text-medical-300">
                <span className="font-medium">Selected:</span>{' '}
                <span className="italic">&ldquo;{editorSelectedText.length > 80 ? editorSelectedText.slice(0, 80) + '...' : editorSelectedText}&rdquo;</span>
              </div>
            )}

            <div className="flex items-end gap-2">
              <textarea
                ref={instructionRef}
                value={instruction}
                onChange={(e) => setInstruction(e.target.value)}
                onKeyDown={handleRevisionKeyDown}
                placeholder={placeholder}
                rows={2}
                disabled={isStreaming}
                className="flex-1 px-3 py-2 text-sm border border-medical-300 dark:border-medical-600 dark:bg-medical-700 dark:text-medical-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
              />
              <button
                onClick={handleRevisionSend}
                disabled={!instruction.trim() || isStreaming}
                className="p-2.5 text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                title="Send (Cmd+Enter)"
              >
                {isStreaming ? <div className="animate-pulse"><Logo size="sm" showText={false} /></div> : <Send className="h-4 w-4" />}
              </button>
            </div>

            <div className="flex items-start gap-1.5 text-[11px] text-medical-400">
              <Lightbulb className="h-3 w-3 flex-shrink-0 mt-0.5" />
              <span>
                {mode === 'write'
                  ? 'Describe what to write from the case sources.'
                  : 'Highlight text in the editor to revise just that section.'}
                {' '}Press <kbd className="px-1 py-0.5 bg-medical-100 dark:bg-medical-700 rounded text-[10px]">Cmd+Enter</kbd> to send.
              </span>
            </div>
          </>
        )}
      </div>

      {/* Scoped styles for markdown preview */}
      <style>{`
        .ai-preview h1, .ai-preview h2, .ai-preview h3 { font-weight: 600; margin-top: 0.75em; margin-bottom: 0.375em; }
        .ai-preview h1 { font-size: 1.25rem; }
        .ai-preview h2 { font-size: 1.125rem; }
        .ai-preview h3 { font-size: 1rem; }
        .ai-preview ul, .ai-preview ol { padding-left: 1.5em; margin: 0.5em 0; }
        .ai-preview ol { list-style-type: decimal; }
        .ai-preview ul { list-style-type: disc; }
        .ai-preview li { margin: 0.25em 0; }
        .ai-preview p { margin: 0.5em 0; }
        .ai-preview p:first-child { margin-top: 0; }
        .ai-preview p:last-child { margin-bottom: 0; }
        .ai-preview strong { font-weight: 600; }
        .ai-preview em { font-style: italic; }
        .ai-preview blockquote { border-left: 3px solid #d1d5db; padding-left: 0.75em; color: #6b7280; margin: 0.5em 0; }
      `}</style>
    </div>
  );
};
